import React, { useState } from 'react';
import './App.css';
import logo from './logo.png';

const deliverySteps = [
  { number: '01', title: 'Scope it', text: 'A focused discovery call turns your idea into a clear, build-ready brief.' },
  { number: '02', title: 'Build it', text: 'Our rapid delivery pod designs, develops and tests your first working release.' },
  { number: '03', title: 'Ship it', text: 'Go live in 2–3 days, then improve with six months of included support.' },
];

const capabilities = [
  { icon: '⌁', title: 'Web platforms', text: 'Customer portals, internal tools, dashboards and responsive business applications.' },
  { icon: '▣', title: 'Mobile apps', text: 'Fast, polished mobile experiences for iOS and Android, built around your users.' },
  { icon: '☁', title: 'Cloud & DevOps', text: 'Cloud foundations, CI/CD pipelines, automation and reliable production operations.' },
  { icon: '◫', title: 'FinOps', text: 'Cloud cost visibility, governance and practical optimisation without slowing teams down.' },
  { icon: '◇', title: 'Security', text: 'Secure-by-design delivery, posture reviews and controls that fit the way you work.' },
  { icon: '◎', title: 'Technical staffing', text: 'Vetted engineers and specialists who join quickly and strengthen your delivery team.' },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const sendBrief = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = encodeURIComponent(`New project brief from ${data.get('name')}`);
    const body = encodeURIComponent(`Name: ${data.get('name')}\nEmail: ${data.get('email')}\n\nProject:\n${data.get('message')}`);
    window.location.href = `mailto:crm@teknocratz.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="container header-inner">
          <a className="brand" href="#top" aria-label="Teknocratz home">
            <img src={logo} alt="Teknocratz" />
          </a>
          <button className="menu-button" aria-label="Toggle menu" onClick={() => setMenuOpen(!menuOpen)}>
            <span /><span />
          </button>
          <nav className={menuOpen ? 'main-nav open' : 'main-nav'} onClick={() => setMenuOpen(false)}>
            <a href="#delivery">Rapid delivery</a>
            <a href="#services">Services</a>
            <a href="#staffing">Staffing</a>
            <a className="nav-cta" href="#contact">Start a project <span>↗</span></a>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-glow glow-one" />
          <div className="hero-glow glow-two" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <div className="eyebrow"><span className="pulse-dot" /> Built fast. Backed for 6 months.</div>
              <h1>Your software.<br /><em>Live in days.</em></h1>
              <p className="hero-lead">From a sharp idea to working web and mobile software in just <strong>2–3 days</strong>—with a dedicated team supporting you long after launch.</p>
              <div className="hero-actions">
                <a href="#contact" className="button button-primary">Get your delivery plan <span>→</span></a>
                <a href="#delivery" className="text-link">See how it works <span>↓</span></a>
              </div>
              <div className="trust-row">
                <div><strong>2–3</strong><span>days to launch</span></div>
                <div><strong>6</strong><span>months support</span></div>
                <div><strong>1</strong><span>team, end to end</span></div>
              </div>
            </div>

            <div className="hero-visual" aria-label="Rapid software delivery overview">
              <div className="orbit orbit-one" />
              <div className="orbit orbit-two" />
              <div className="product-window">
                <div className="window-bar"><i /><i /><i /><span>your-product.app</span></div>
                <div className="window-body">
                  <div className="mini-sidebar"><b>T</b><i /><i /><i /></div>
                  <div className="mini-content">
                    <div className="mini-top"><span>Product dashboard</span><b>Live</b></div>
                    <div className="mini-chart"><i /><i /><i /><i /><i /><i /><i /></div>
                    <div className="mini-cards"><span /><span /><span /></div>
                  </div>
                </div>
              </div>
              <div className="float-card ship-card"><span className="check">✓</span><div><b>Ready to ship</b><small>QA passed · Today</small></div></div>
              <div className="float-card support-card"><span>6</span><div><b>Months</b><small>Support included</small></div></div>
            </div>
          </div>
        </section>

        <section className="delivery" id="delivery">
          <div className="container">
            <div className="section-heading split-heading">
              <div><span className="kicker">The rapid delivery model</span><h2>From brief to launch<br />before the week is over.</h2></div>
              <p>Speed does not have to mean fragile. A focused scope, an experienced pod and a proven build process let us move quickly while keeping quality high.</p>
            </div>
            <div className="steps-grid">
              {deliverySteps.map((step) => (
                <article className="step-card" key={step.number}>
                  <span className="step-number">{step.number}</span>
                  <div className="step-line" />
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
            <div className="support-strip">
              <div className="support-icon">↻</div>
              <div><span>After launch</span><h3>Six months of support is included.</h3></div>
              <p>Bug fixes, technical guidance and product improvements—so your software keeps working as your needs evolve.</p>
              <a href="#contact">Talk to our team →</a>
            </div>
          </div>
        </section>

        <section className="services" id="services">
          <div className="container">
            <div className="section-heading centered">
              <span className="kicker">One technology partner</span>
              <h2>Build it. Run it. Scale it.</h2>
              <p>Product delivery and the specialist expertise to keep your technology secure, efficient and ready for growth.</p>
            </div>
            <div className="capability-grid">
              {capabilities.map((item, index) => (
                <article className={`capability-card card-${index + 1}`} key={item.title}>
                  <div className="capability-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                  <a href="#contact" aria-label={`Discuss ${item.title}`}>Explore <span>↗</span></a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="staffing" id="staffing">
          <div className="container staffing-grid">
            <div className="staffing-copy">
              <span className="kicker light">Technical staffing</span>
              <h2>Add the skills.<br />Skip the hiring drag.</h2>
              <p>Bootstrap a new service or add missing expertise to an existing team. We place practical, delivery-ready professionals who can contribute from day one.</p>
              <div className="skill-pills"><span>Cloud engineers</span><span>DevOps specialists</span><span>FinOps analysts</span><span>Security engineers</span><span>App developers</span><span>Technical leads</span></div>
              <a href="#contact" className="button button-light">Find your specialist <span>→</span></a>
            </div>
            <div className="talent-panel">
              <div className="talent-head"><span>Available expertise</span><span className="available"><i /> Ready</span></div>
              {['Cloud architecture', 'DevOps & automation', 'FinOps optimisation', 'Application security'].map((skill, index) => (
                <div className="talent-row" key={skill}>
                  <div className={`avatar avatar-${index + 1}`}>{['CA', 'DO', 'FO', 'AS'][index]}</div>
                  <div><b>{skill}</b><small>Vetted specialist</small></div>
                  <span className="arrow">↗</span>
                </div>
              ))}
              <div className="talent-footer"><strong>Flexible engagement</strong><span>Short-term · Long-term · Team pods</span></div>
            </div>
          </div>
        </section>

        <section className="contact" id="contact">
          <div className="container contact-grid">
            <div className="contact-copy">
              <span className="kicker">Have something to build?</span>
              <h2>Let’s get it moving.</h2>
              <p>Tell us what you need. We’ll respond with the fastest practical route from idea to working software—or the right technical talent for your team.</p>
              <a className="email-link" href="mailto:crm@teknocratz.com"><span>@</span><div><small>Email us directly</small><strong>crm@teknocratz.com</strong></div></a>
            </div>
            <form className="brief-form" onSubmit={sendBrief}>
              <div className="field-row"><label>Your name<input required name="name" type="text" placeholder="Jane Smith" /></label><label>Work email<input required name="email" type="email" placeholder="jane@company.com" /></label></div>
              <label>What can we help you deliver?<textarea required name="message" rows="4" placeholder="A mobile app, cloud migration, a DevOps specialist…" /></label>
              <button className="button button-primary" type="submit">Send project brief <span>→</span></button>
              <small className="form-note">No lengthy sales process. Just a useful first conversation.</small>
            </form>
          </div>
        </section>
      </main>

      <footer>
        <div className="container footer-main">
          <div className="footer-brand"><img src={logo} alt="Teknocratz" /><p>Rapid software delivery.<br />Reliable technical expertise.</p></div>
          <div><strong>Build</strong><a href="#delivery">Rapid delivery</a><a href="#services">Web applications</a><a href="#services">Mobile apps</a></div>
          <div><strong>Operate</strong><a href="#services">Cloud & DevOps</a><a href="#services">FinOps</a><a href="#services">Security</a></div>
          <div><strong>Grow</strong><a href="#staffing">Technical staffing</a><a href="#contact">Start a project</a><a href="mailto:crm@teknocratz.com">Contact</a></div>
        </div>
        <div className="container footer-bottom"><span>© {new Date().getFullYear()} Teknocratz. Built for what’s next.</span><a href="#top">Back to top ↑</a></div>
      </footer>
    </div>
  );
}

export default App;
